import StatusChip from 'components/@extended/StatusChip';
import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import Loader from 'components/Loader';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Tooltip } from 'components/ui/tooltip';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useGetComplianceBlockingRulesQuery,
    useUpdateComplianceBlockingRuleMutation,
} from 'store/api/compliance-blocking-rules';
import {
    ComplianceBlockingRuleDto,
    ComplianceBlockingRuleUpdateDto,
    ComplianceBlockingSeverity,
} from 'types/compliance';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { CONFIGURATION_CARD_CLASS } from './configurationCard';

/** The rules tables' cells at the compact density: 8px of padding, the row's ends still 16px in. */
const HEAD_CELL_CLASS = cn(SIMPLE_TABLE_HEAD_CLASS, 'p-2 font-semibold');
const FLAG_HEAD_CELL_CLASS = cn(HEAD_CELL_CLASS, 'text-center whitespace-nowrap');
const BODY_CELL_CLASS = cn(SIMPLE_TABLE_CELL_CLASS, 'p-2');

const severityChipType: Record<ComplianceBlockingSeverity, 'critical' | 'error' | 'warning' | 'info'> = {
    [ComplianceBlockingSeverity.CRITICAL]: 'critical',
    [ComplianceBlockingSeverity.HIGH]: 'error',
    [ComplianceBlockingSeverity.MEDIUM]: 'warning',
    [ComplianceBlockingSeverity.LOW]: 'info',
};

const groupRulesByCategory = (rules: ComplianceBlockingRuleDto[]) => {
    const grouped = new Map<string, ComplianceBlockingRuleDto[]>();
    for (const rule of rules) {
        const category = rule.category;
        if (!grouped.has(category)) {
            grouped.set(category, []);
        }
        grouped.get(category)!.push(rule);
    }
    return grouped;
};

type BooleanField =
    | 'isEnabled'
    | 'blocksCases'
    | 'blocksTimecards'
    | 'blocksAmlTimecards'
    | 'requireAck'
    | 'requireAckHighRisk';
type EditableField = BooleanField | 'enforcementGracePeriodDays';
type RuleEdit = Partial<Pick<ComplianceBlockingRuleUpdateDto, EditableField>>;
type EditedRules = Record<number, RuleEdit>;

/** The column heading of each flag, which also names its checkbox. */
const FIELD_LABEL_IDS: Record<Exclude<BooleanField, 'isEnabled'>, string> = {
    blocksCases: 'configuration.compliance-rule-blocks-cases',
    blocksTimecards: 'configuration.compliance-rule-blocks-timecards',
    blocksAmlTimecards: 'configuration.compliance-rule-blocks-aml-timecards',
    requireAck: 'configuration.compliance-rule-require-ack',
    requireAckHighRisk: 'configuration.compliance-rule-require-ack-high-risk',
};

const ComplianceBlockingRules = () => {
    const { data: rules, isLoading } = useGetComplianceBlockingRulesQuery();
    const [updateRule] = useUpdateComplianceBlockingRuleMutation();

    const [isEditMode, setIsEditMode] = useState(false);
    const [editedRules, setEditedRules] = useState<EditedRules>({});
    const [isSaving, setIsSaving] = useState(false);

    const invalidRuleIds = useMemo(() => {
        if (!rules) return [] as number[];
        return rules
            .map((rule) => {
                const changes = editedRules[rule.id];
                if (!changes) return null;
                const isEnabled = changes.isEnabled ?? rule.isEnabled;
                const blocksCases = changes.blocksCases ?? rule.blocksCases;
                const blocksTimecards = changes.blocksTimecards ?? rule.blocksTimecards;
                const blocksAmlTimecards = changes.blocksAmlTimecards ?? rule.blocksAmlTimecards;
                if (isEnabled && !blocksCases && !blocksTimecards && !blocksAmlTimecards) return rule.id;
                return null;
            })
            .filter((id): id is number => id !== null);
    }, [rules, editedRules]);

    const handleToggle = useCallback((ruleId: number, field: BooleanField, currentValue: boolean) => {
        setEditedRules((prev) => {
            const existing = prev[ruleId] ?? {};
            const updated: RuleEdit = { ...existing, [field]: !currentValue };
            // Turning AML off clears the per-rule grace — grace only applies while AML is on.
            if (field === 'blocksAmlTimecards' && currentValue) {
                updated.enforcementGracePeriodDays = null;
            }
            return { ...prev, [ruleId]: updated };
        });
    }, []);

    const handleGraceChange = useCallback((ruleId: number, raw: string) => {
        setEditedRules((prev) => {
            const existing = prev[ruleId] ?? {};
            const trimmed = raw.trim();
            const parsed = trimmed === '' ? null : Number(trimmed);
            // Grace period is a whole-day count — reject fractional / non-integer input (1.5, 2e1).
            const next: number | null = parsed != null && Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
            return { ...prev, [ruleId]: { ...existing, enforcementGracePeriodDays: next } };
        });
    }, []);

    const handleCancel = useCallback(() => {
        setEditedRules({});
        setIsEditMode(false);
    }, []);

    const handleSave = useCallback(async () => {
        if (!rules) return;

        const changedEntries = Object.entries(editedRules).filter(([, changes]) => Object.keys(changes).length > 0);
        if (changedEntries.length === 0) {
            setIsEditMode(false);
            return;
        }

        setIsSaving(true);
        try {
            await Promise.all(
                changedEntries.map(([idStr, changes]) => {
                    const id = Number(idStr);
                    const original = rules.find((r) => r.id === id);
                    if (!original) return Promise.resolve();

                    const dto: ComplianceBlockingRuleUpdateDto = {
                        id,
                        isEnabled: changes.isEnabled ?? original.isEnabled,
                        blocksCases: changes.blocksCases ?? original.blocksCases,
                        blocksTimecards: changes.blocksTimecards ?? original.blocksTimecards,
                        blocksAmlTimecards: changes.blocksAmlTimecards ?? original.blocksAmlTimecards,
                        enforcementGracePeriodDays:
                            'enforcementGracePeriodDays' in changes
                                ? (changes.enforcementGracePeriodDays ?? null)
                                : original.enforcementGracePeriodDays,
                        requireAck: changes.requireAck ?? original.requireAck,
                        requireAckHighRisk: changes.requireAckHighRisk ?? original.requireAckHighRisk,
                        severity: original.severity,
                    };
                    return updateRule(dto).unwrap();
                })
            );
            setEditedRules({});
            setIsEditMode(false);
        } catch (e) {
            console.error('Update error:', e);
            errorAlert(t('forms.error-submitting-the-form'));
        } finally {
            setIsSaving(false);
        }
    }, [rules, editedRules, updateRule]);

    if (isLoading) {
        return <Loader />;
    }

    const grouped = rules ? groupRulesByCategory(rules) : new Map<string, ComplianceBlockingRuleDto[]>();

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.compliance-blocking-rules" />
                </h3>
                {isEditMode ? (
                    <div className="flex gap-2">
                        <CancelButton onClick={handleCancel} disabled={isSaving} />
                        <SaveButton
                            onClick={() => {
                                void handleSave();
                            }}
                            disabled={isSaving || invalidRuleIds.length > 0}
                        />
                    </div>
                ) : (
                    <PermissionButton
                        permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                        variant="outlined"
                        size="small"
                        onClick={() => setIsEditMode(true)}
                    >
                        <FormattedMessage id="edit" />
                    </PermissionButton>
                )}
            </div>
            <div className="new:gap-(--card-gap) flex flex-col gap-4 empty:hidden">
                {Array.from(grouped).map(
                    ([category, categoryRules]) =>
                        categoryRules.length > 0 && (
                            <div key={category} className={CONFIGURATION_CARD_CLASS}>
                                <div className="relative w-full overflow-auto">
                                    <table className="w-full text-left text-sm">
                                        <TableHeader className="[&_tr]:border-b-0">
                                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                <TableHead className={cn(HEAD_CELL_CLASS, 'w-[1%]')} />
                                                <TableHead className={HEAD_CELL_CLASS}>
                                                    <FormattedMessage
                                                        id={`configuration.compliance-category.${category}`}
                                                    />
                                                </TableHead>
                                                <TableHead className={cn(FLAG_HEAD_CELL_CLASS, 'w-[120px]')}>
                                                    <FormattedMessage id={FIELD_LABEL_IDS.blocksCases} />
                                                </TableHead>
                                                <TableHead className={cn(FLAG_HEAD_CELL_CLASS, 'w-[120px]')}>
                                                    <FormattedMessage id={FIELD_LABEL_IDS.blocksTimecards} />
                                                </TableHead>
                                                <TableHead className={cn(FLAG_HEAD_CELL_CLASS, 'w-[180px]')}>
                                                    <FormattedMessage id={FIELD_LABEL_IDS.blocksAmlTimecards} />
                                                </TableHead>
                                                <TableHead className={cn(FLAG_HEAD_CELL_CLASS, 'w-[120px]')}>
                                                    <FormattedMessage id={FIELD_LABEL_IDS.requireAck} />
                                                </TableHead>
                                                <TableHead className={cn(FLAG_HEAD_CELL_CLASS, 'w-[120px]')}>
                                                    <FormattedMessage id={FIELD_LABEL_IDS.requireAckHighRisk} />
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {categoryRules.map((rule) => (
                                                <ComplianceBlockingRuleRow
                                                    key={rule.id}
                                                    rule={rule}
                                                    isEditMode={isEditMode}
                                                    editedFields={editedRules[rule.id]}
                                                    onToggle={handleToggle}
                                                    onGraceChange={handleGraceChange}
                                                    isInvalid={invalidRuleIds.includes(rule.id)}
                                                />
                                            ))}
                                        </TableBody>
                                    </table>
                                </div>
                            </div>
                        )
                )}
            </div>
        </div>
    );
};

export default ComplianceBlockingRules;

interface RuleRowProps {
    rule: ComplianceBlockingRuleDto;
    isEditMode: boolean;
    editedFields?: RuleEdit;
    onToggle: (ruleId: number, field: BooleanField, currentValue: boolean) => void;
    onGraceChange: (ruleId: number, raw: string) => void;
    isInvalid: boolean;
}

const SIMPLE_BLOCK_FIELDS = ['blocksCases', 'blocksTimecards'] as const;
const ACK_FIELDS = ['requireAck', 'requireAckHighRisk'] as const;

const ComplianceBlockingRuleRow: React.FC<RuleRowProps> = ({
    rule,
    isEditMode,
    editedFields,
    onToggle,
    onGraceChange,
    isInvalid,
}) => {
    const getValue = (field: BooleanField) => editedFields?.[field] ?? rule[field];
    const graceValue =
        editedFields && 'enforcementGracePeriodDays' in editedFields
            ? editedFields.enforcementGracePeriodDays
            : rule.enforcementGracePeriodDays;
    const validationMessage = t('configuration.compliance-rule-must-block-something');
    const ruleLabel = t(`compliance.blocking-rule.${rule.ruleCode}`);

    const renderCheckbox = (field: BooleanField, labelId: string) => (
        <Checkbox
            className="mx-auto"
            checked={getValue(field)}
            disabled={!isEditMode}
            aria-label={`${ruleLabel}: ${t(labelId)}`}
            onCheckedChange={() => onToggle(rule.id, field, getValue(field))}
        />
    );

    return (
        <TableRow
            className={cn(
                SIMPLE_TABLE_ROW_CLASS,
                !isEditMode && !getValue('isEnabled') && 'opacity-50',
                isInvalid &&
                    'bg-error-lighter hover:bg-error-lighter [&>td:first-of-type]:shadow-[inset_3px_0_0_0_var(--color-error)]'
            )}
        >
            <TableCell className={BODY_CELL_CLASS}>
                <Checkbox
                    className="mx-auto"
                    checked={getValue('isEnabled')}
                    disabled={!isEditMode}
                    aria-label={ruleLabel}
                    onCheckedChange={() => onToggle(rule.id, 'isEnabled', getValue('isEnabled'))}
                />
            </TableCell>
            <TableCell className={BODY_CELL_CLASS}>
                <div className="flex items-center gap-2">
                    <p className="text-body1 font-semibold">{ruleLabel}</p>
                    <StatusChip
                        label={t(`contacts.blocking.severity.${rule.severity.toLowerCase()}`)}
                        type={severityChipType[rule.severity]}
                        size="small"
                    />
                </div>
                <span className="text-caption text-fg-secondary mt-0.5 block">
                    {t(`compliance.blocking-rule-description.${rule.ruleCode}`)}
                </span>
                {isInvalid && <span className="text-caption text-error mt-0.5 block">{validationMessage}</span>}
            </TableCell>
            {SIMPLE_BLOCK_FIELDS.map((field) => (
                <TableCell key={field} className={cn(BODY_CELL_CLASS, 'text-center')}>
                    <ValidationTooltip message={isInvalid ? validationMessage : undefined}>
                        {renderCheckbox(field, FIELD_LABEL_IDS[field])}
                    </ValidationTooltip>
                </TableCell>
            ))}
            <TableCell className={cn(BODY_CELL_CLASS, 'text-center')}>
                <div className="inline-flex items-center gap-1.5">
                    <ValidationTooltip message={isInvalid ? validationMessage : undefined}>
                        {renderCheckbox('blocksAmlTimecards', FIELD_LABEL_IDS.blocksAmlTimecards)}
                    </ValidationTooltip>
                    {getValue('blocksAmlTimecards') &&
                        (isEditMode ? (
                            <>
                                <Input
                                    type="number"
                                    value={graceValue ?? ''}
                                    onChange={(e) => onGraceChange(rule.id, e.target.value)}
                                    placeholder="0"
                                    min={0}
                                    step={1}
                                    aria-label={t('configuration.compliance-rule-grace-period-days')}
                                    className="bg-paper h-6 w-[50px] px-1.5 text-right text-base/[1.25] md:text-[13px]"
                                />
                                <span className="text-caption text-fg-secondary">
                                    {t('configuration.compliance-rule-grace-period-days-suffix')}
                                </span>
                            </>
                        ) : graceValue != null && graceValue > 0 ? (
                            <span className="text-caption text-fg-secondary">
                                {t('configuration.compliance-rule-grace-period-readonly', {
                                    days: String(graceValue),
                                })}
                            </span>
                        ) : null)}
                </div>
            </TableCell>
            {ACK_FIELDS.map((field) => (
                <TableCell key={field} className={cn(BODY_CELL_CLASS, 'text-center')}>
                    {renderCheckbox(field, FIELD_LABEL_IDS[field])}
                </TableCell>
            ))}
        </TableRow>
    );
};

/**
 * The rule's validation message on hover, only while there is one. The tooltip stays mounted either
 * way, so the checkbox inside it keeps its focus when a click makes the rule valid or invalid.
 */
function ValidationTooltip({ message, children }: { message?: string; children: ReactElement }) {
    const [open, setOpen] = useState(false);
    return (
        <Tooltip content={message} open={!!message && open} onOpenChange={setOpen}>
            {children}
        </Tooltip>
    );
}
